<template>
    <div class="fixed inset-0 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg px-6 py-10 md:p-12 w-full max-w-4xl relative overflow-y-auto max-h-[90vh] md:max-h-[85vh]">

            <!-- ボタン無効化時の説明テキスト -->
            <div v-if="!props.isUploaded" class="items-center mb-4">
                <div
                  class="bg-[#f3f4f6] border border-[#d1d5db] border-solid flex gap-3 items-center p-4 rounded relative w-full"
                >
                    <div class="relative shrink-0 w-6 h-6">
                        <img
                            :src="spinner"
                            alt=""
                            class="block max-w-none w-full h-full"
                        />
                    </div>
                    <p class="leading-6 text-sub text-base whitespace-nowrap">
                        動画アップロード中…
                    </p>
                </div>
            </div>
            <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />
            <div class="flex justify-between items-center border-b-2 pb-2 mb-4">
                <h3 class="text-xl font-bold">詳細情報を入力する</h3>
                <button @click="emit('close-modal')" class="link-styles--gray">
                    <i class="fa-light fa-xmark text-3xl"></i>
                </button>
            </div>
            <!-- 入力欄の内容 -->
            <div class="space-y-2 mb-6">
                <label class="block text-sm font-bold">YouTube動画のURL <span class="text-xs text-alert font-bold">必須</span></label>
                <input
                    type="url"
                    v-model="video.url_youtube"
                    @blur="validateField('youtubeUrl')"
                    placeholder="例）https://www.youtube.com/watch?v=xxxxx"
                    class="placeholder-placeholder border p-2 rounded w-full"
                    :class="{'border-red-500': errors?.youtubeUrl}"
                >
                <p v-if="showError('youtubeUrl')" class="text-alert text-sm font-bold">{{ errors.youtubeUrl }}</p>
            </div>

            <div class="mb-6">
                <label class="block text-sm font-bold">YouTubeの動画タイトル <span class="text-xs text-alert font-bold">必須</span></label>
                <textarea
                    v-model="video.video_title_youtube"
                    @blur="validateField('youtubeTitle')"
                    placeholder="YouTubeと同じタイトルを入力してください"
                    class="placeholder-placeholder border p-2 rounded w-full mt-2"
                    :class="{'border-red-500': errors.youtubeTitle}"
                    rows="2"
                    maxlength="100">
                </textarea>
                <div class="text-right text-xs">{{ video.video_title_youtube?.length }}/100</div>
                <p v-if="errors?.youtubeTitle" class="text-alert text-sm font-bold">{{ errors.youtubeTitle }}</p>
            </div>

            <div class="space-y-2 mb-6">
                <label class="block text-sm font-bold">動画内の出演者(肩書き) <span class="text-xs text-alert font-bold">必須</span></label>
                <span class="min-w-0 break-words text-xs">※複数入力する場合は、半角カンマ（,）で区切って入力してください。</span>
                <input type="text"
                    v-model="video.speaker"
                    @blur="validateField('speakerName')"
                    placeholder="出演者(肩書き),出演者(肩書き),出演者(肩書き)"
                    class="placeholder-placeholder border p-2 rounded w-full"
                    :class="{'border-red-500': errors.speakerName}">
                <p v-if="showError('speakerName')" class="text-alert text-sm font-bold">{{ errors.speakerName }}</p>
            </div>

            <div class="mb-6">
                <label class="block text-sm font-bold">動画の補足情報</label>
                <textarea
                    v-model="video.note"
                    placeholder="補足がある場合は入力してください"
                    maxlength="500"
                    class="placeholder-placeholder border p-2 mt-2 h-28 rounded w-full"></textarea>
                    <div class="grid grid-cols-[4fr_1fr] items-start gap-3">
                        <span class="min-w-0 break-words text-xs">※動画の補足情報を入力すると、より正確な記事を作成できます（記入例は<a href="https://note.com/livedoor_echoes/n/nc8c196c26252" class="link-styles" target="_blank" rel="noopener noreferrer"
>こちら</a>）</span>
                        <span class="justify-self-end self-start whitespace-nowrap text-xs">{{ video.note?.length }}/500</span>
                    </div>
            </div>

            <div class="flex justify-end mt-4 border-t">
                <button :disabled="!canSubmit || props.videoId === null" @click="submitAdditionalInfo" class="w-full md:w-auto mt-4 btn-primary disabled:btn-disabled">動画を登録する</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, toRef, getCurrentInstance, defineProps, defineEmits, inject, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import ErrorMessage from "../ErrorMessage.vue";
import spinner from "@/assets/spinner.svg"

const props = defineProps({
    video: {
        type: Object,
        default: () => ({})
    },
    isUploaded: {
        type: Boolean,
        default: false
    },
});
const video = toRef(props, 'video');
const fetchChannels = inject("fetchChannels");
const store = useStore();
const router = useRouter();

const accessToken = store.state.accessToken;
const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;

const errorMessage = ref("");
const isSubmitting = ref(false); // 二重投稿防止

const canSubmit = computed(() => {
    // すべての必須フィールドに値が入力されているかどうかを確認
    if(!video.value) {
        return false
    }

    const hasRequiredFields = video.value.url_youtube && video.value.video_title_youtube && video.value.speaker;
    const hasNoErrors = !errors.value.youtubeUrl && !errors.value.youtubeTitle && !errors.value.speakerName;
    return hasRequiredFields && hasNoErrors && !isSubmitting.value && props.videoId !== null;
});

const showError = (fieldName) => {
    return touched.value[fieldName] && errors.value[fieldName];
}

const errors = ref({
    youtubeUrl: null,
    youtubeTitle: null,
    speakerName: null,
});

const touched = ref({
    youtubeUrl: false,
    youtubeTitle: false,
    speakerName: false,
});

const emit = defineEmits(['close-modal', 'submit-additional-info', 'update:video']);

const validateField = (fieldName) => {
    if (fieldName === 'youtubeUrl') {
        if (!video.value.url_youtube) {
            errors.value.youtubeUrl = 'YouTubeの動画URLは必須です。';
        } else if (!video.value.url_youtube.startsWith('https://')) {
            errors.value.youtubeUrl = 'URLはhttps://から始まる形式で入力してください。';
        } else if (video.value.url_youtube.includes(' ') || video.value.url_youtube.includes('\u3000')) {
            errors.value.youtubeUrl = 'URLにスペースが含まれています。';
        } else {
            errors.value.youtubeUrl = null;
        }
    } else if (fieldName === 'youtubeTitle') {
        errors.value.youtubeTitle = !video.value.video_title_youtube ? 'YouTubeの動画タイトルは必須です。' : null;
        // YouTubeタイトルの文字数制限もここでチェック
        if (video.value.title_youtube && video.value.title_youtube.length > 100) {
            errors.value.youtubeTitle = '文字数が超過しています。';
        }
    } else if (fieldName === 'speakerName') {
        errors.value.speakerName = !video.value.speaker ? '動画内の発言者名は必須です。' : null;
    }
    touched.value[fieldName] = true; // フィールドをタッチとしてマーク
    emit("update:video", video);
};

// Toast コンポーネントを使用するためのヘルパー関数
const createToast = (message, type) => {
    // Vue 3で提供されているグローバルプロパティ$toastを使用
    // コンポーネント内では直接this.$toastは使えないため、ルートインスタンスからアクセス
    const app = getCurrentInstance();
    if (app && app.appContext.config.globalProperties.$toast) {
        app.appContext.config.globalProperties.$toast[type](message, { duration: 10000 } );
    } else {
        // フォールバック：トーストコンポーネントが利用できない場合
        console.warn(message);
        if (type === 'error') {
            alert(message);
        }
    }
};

// 追加情報送信
const submitAdditionalInfo = async () => {
    if (!video.value.url_youtube || !video.value.video_title_youtube) {
        errorMessage.value = '必要な情報を入力してください。';
        return;
    }
    if (!video.value.url_youtube.startsWith('https://')) {
        errorMessage.value = 'URLはhttps://から始まる形式で入力してください。';
        return;
    }
    if (video.value.url_youtube.includes(' ') || video.value.url_youtube.includes('\u3000')) {
        errorMessage.value = 'URLにスペースが含まれています。';
        return;
    }
    try {
        const response = await axios.post(`${apiEndpoint}/add-videodata/`, {
            video_id: video.value.video_id,
            url_youtube: video.value.url_youtube,
            video_title_youtube: video.value.video_title_youtube,
            speaker: video.value.speaker,
            job_title: video.value.job_title,
            note: video.value.note,
        }, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        // トークン状態の確認
        if (response.data.token_status) {
            const tokenStatus = response.data.token_status;
            if (tokenStatus.token_status === "expired") {
                createToast('セッションの有効期限が切れました。再ログインしてください。', 'error');
                // ログアウト処理
                store.dispatch('logout').then(() => {
                        // ログイン画面にリダイレクト
                        router.push({
                            name: 'Login',
                            query: { expired: 'token' }
                        });
                    });
                return;
            } else if (tokenStatus.token_status === "warning") {
                // 警告表示
                createToast(tokenStatus.message, 'warning');
            }
        }

        resetForm(); // フォームのリセットとバリデーションエラーのクリア
        emit('submit-additional-info');
    } catch (error) {
        if (error.response.status == 403) {
            errorMessage.value = 'チャンネルに対するアクセス権がありません。チャンネル管理者にお問い合わせください。';
            fetchChannels();
        } else {
            errorMessage.value = '送信中にエラーが発生しました。時間を空けて再度お試しください。';
        }
    }
};

// フォームリセットとエラー状態のクリア関数
const resetForm = () => {
    errors.value = {
        youtubeUrl: null,
        youtubeTitle: null,
        speakerName: null
    };
    touched.value = {
        youtubeUrl: false,
        youtubeTitle: false,
        speakerName: false
    };
};

</script>
